import { Bell } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, Callout, RoleBadge, pathFor,
  Diagram, MockField, MockSelect, MockButton, MockRow,
} from "../shared";

export default function SettingsAlertDispatcherSection() {
  return (
    <div data-testid="guide-settings-alert-dispatcher">
      <PageHeading
        icon={Bell}
        title="IT Alert Dispatcher"
        description="A way to get an email when something serious goes wrong, so the IT person doesn't have to keep watching the diagnostics log all day. You list who should be told, and how noisy the alerts are allowed to be."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="it_admin" />
        <span className="text-xs text-white/40">Needs the <span className="font-mono text-white/70">manage_smtp</span> permission (alerts use the same mailbox). Sending a test alert asks for your password first.</span>
      </div>

      <Callout variant="info" title="In plain words">
        The system keeps a private log of errors. This feature is the doorbell on top of that log: when a bad-enough error happens, it emails the people you've listed. You decide who gets pinged and how often.
      </Callout>

      <Section title="When do I use this?">
        <p className="text-sm text-white/65 leading-relaxed">
          Set it up once so the on-call IT person is told about real problems automatically. You'll come back to it to change the recipient list or to test that alerts still arrive.
        </p>
      </Section>

      <Section title="Where it lives">
        <p className="text-sm text-white/65 leading-relaxed">
          It sits inside the <span className="text-white">Email Settings</span> tab, just below the email card, in a box called <span className="text-white">IT Alert Recipients</span>. It's stored there because it shares the same mailbox login, the same permission, and the same on/off behaviour — if email is switched off, alerts are off too.
        </p>
      </Section>

      <Section title="The screen">
        <Diagram
          frameLabel="Settings → Email → IT Alert Recipients"
          legend={[
            <>Type the email addresses that should be told, separated by commas.</>,
            <>Choose how serious a problem has to be before an email goes out.</>,
            <>Set the quiet window so the same error doesn't email everyone over and over.</>,
            <>Press <span className="text-white">Send Test Alert</span> to confirm it works.</>,
          ]}
        >
          <MockField label="Alert recipients" value="it@example.com, oncall@example.com" marker={1} />
          <MockRow>
            <MockSelect label="Severity threshold" value="Critical only" marker={2} />
            <MockField label="Throttle window (mins)" value="15" marker={3} />
          </MockRow>
          <MockButton label="Send Test Alert" marker={4} variant="ghost" />
        </Diagram>
      </Section>

      <Section title="Each field explained">
        <div className="space-y-2">
          <Field name="Alert recipients" required="required"><p>A comma-separated list of email addresses. Everyone listed gets every alert and the daily summary.</p></Field>
          <Field name="Severity threshold" required="required"><p><span className="text-white">Critical</span> (the default) emails only on the worst problems. <span className="text-white">Error</span> emails on errors and worse. Info and Warning never email.</p></Field>
          <Field name="Throttle window (minutes)" required="required"><p>Between 1 and 120, default <span className="text-white">15</span>. The first time a particular error happens you're emailed straight away; repeats within the window are counted and rolled into one follow-up "X more happened" email when the window ends.</p></Field>
          <Field name="Daily digest enabled" required="optional"><p>If on, a once-a-day summary email goes out even when nothing crossed the alert line.</p></Field>
          <Field name="Daily digest hour" required="optional"><p>0–23, the hour the summary is sent. It's designed to send once a day. Note: restarting the server during that hour can occasionally cause one duplicate summary — a known, acceptable quirk.</p></Field>
        </div>
      </Section>

      <Section title="How it keeps alerts from becoming spam">
        <ul className="list-disc list-inside text-xs text-white/65 space-y-1">
          <li>Identical errors are grouped together so you aren't emailed once per repeat.</li>
          <li>Problems with sending email never trigger an alert — that would cause an endless loop if the mailbox itself was broken.</li>
          <li>There's a hard ceiling of <span className="text-white">20 emails per hour</span>. Beyond that, alerts are held back and folded into the next summary.</li>
          <li>These limits live in the server's memory, so a restart resets them and may cause one repeat alert during an ongoing problem — an accepted trade-off for now.</li>
        </ul>
      </Section>

      <Section title="How to test it">
        <Steps steps={[
          <>Make sure email is set up and the master Enabled switch is on.</>,
          <>Type at least one address in <span className="text-white">Alert recipients</span>.</>,
          <>Press <span className="text-white">Send Test Alert</span> and confirm with your password.</>,
          <>The test ignores the quiet window and the hourly ceiling, so it always arrives. Each person should get an email within seconds. If not, check the System Diagnostics Log (Email category) for the reason.</>,
        ]} />
      </Section>

      <Callout variant="warning" title="No recipients means no alerts">
        If you leave the recipients box empty, alerting is silently switched off. The log still records everything — only the emails stop.
      </Callout>
      <Callout variant="info" title="Pair it with the diagnostics log">
        Alerts just tell you something happened. The full place to investigate is the <Link href={pathFor("settings/system-log")} className="text-cyan-300 hover:underline">System Diagnostics Log</Link>.
      </Callout>
    </div>
  );
}
